import { getUser } from '../services/auth'
import { formatDateString, formatTimeString } from './commonColumns'

type DateValue = string | number | Date

export const formatUserDateTime = (value: DateValue, includeTime: boolean = true): string => {
  if (!value) return '-'

  try {
    let date: Date

    if (typeof value === 'string') {
      date = new Date(value)
    } else if (typeof value === 'number') {
      date = new Date(value)
    } else {
      date = value
    }

    if (isNaN(date.getTime())) {
      return '-'
    }

    const user = getUser()
    const dateFormat = user?.dateFormat ?? 'MM/dd/yyyy'
    const formattedDate = formatDateString(date, dateFormat)

    if (includeTime) {
      const timeFormat = user?.timeFormat ?? ':'
      const is24h = user?.timeFormat24h ?? false
      const formattedTime = formatTimeString(date, timeFormat, is24h)
      return `${formattedDate} ${formattedTime}`
    }

    return formattedDate
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

const resolveUser = (user?: { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null) => {
  return user ?? getUser()
}

/**
 * Convert user's dateFormat (e.g. 'MM/dd/yyyy') to a react-datepicker / date-fns format string.
 * react-datepicker uses the same tokens as date-fns, which happen to match the API value directly.
 */
export const getDatePickerFormat = (user?: { dateFormat?: string } | null): string => {
  const resolved = resolveUser(user)
  return resolved?.dateFormat ?? 'MM/dd/yyyy'
}

/**
 * Build a react-datepicker datetime format using current user date/time preferences.
 */
export const getDateTimePickerFormat = (
  user?: { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null
): string => {
  const resolved = resolveUser(user)
  const datePart = getDatePickerFormat(resolved)
  const separator = resolved?.timeFormat ?? ':'
  const timePart = resolved?.timeFormat24h ? `HH${separator}mm${separator}ss` : `hh${separator}mm${separator}ss aa`
  return `${datePart} ${timePart}`
}

/**
 * Convert user's dateFormat to a moment.js format string (used by @mdsol/lego DatePicker).
 * Moment uses uppercase DD/MM/YYYY whereas date-fns/API uses dd/MM/yyyy.
 */
export const getMomentDateFormat = (user?: { dateFormat?: string } | null): string => {
  const resolved = resolveUser(user)
  const fmt = resolved?.dateFormat ?? 'MM/dd/yyyy'
  // Convert date-fns tokens → moment tokens: dd→DD, yyyy→YYYY
  return fmt.replace(/dd/g, 'DD').replace(/yyyy/g, 'YYYY')
}

/**
 * All supported date formats expressed as moment.js format strings.
 * Covers the 9 app display formats plus common numeric-dash and numeric-dot
 * variants that users commonly type (e.g. 09-09-2025, 2025-09-09, 09.09.2025).
 *
 * Used as the DatePicker `dateFormat` array: lego/react-datepicker displays using
 * the first entry and tries ALL entries when parsing typed input — so a user can
 * type in any common format even when the app is configured to a different one.
 */
const ALL_MOMENT_DATE_FORMATS = [
  // App display formats
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'DD MMM, YYYY',
  'MMM DD, YYYY',
  'DD-MMM-YYYY',
  'MMM-DD-YYYY',
  'DDMMMYYYY',
  'YYYYMMDD',
  // Numeric dash variants (very common for typed input, e.g. 09-09-2025)
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YYYY-MM-DD',
  // Numeric dot variants (also common)
  'DD.MM.YYYY',
  'MM.DD.YYYY',
  'YYYY.MM.DD'
]

/**
 * Returns the DatePicker `dateFormat` as an array (moment.js tokens — for @mdsol/lego DatePicker).
 * The user's configured format is first (used for display), followed by all other
 * supported formats so that typed input is parsed correctly regardless of what
 * the user types (e.g. typing "10/10/2025" when the display format is "10-Oct-2025").
 */
export const getDatePickerParseFormats = (user?: { dateFormat?: string } | null): string[] => {
  const primary = getMomentDateFormat(user)
  return [primary, ...ALL_MOMENT_DATE_FORMATS.filter((f) => f !== primary)]
}

/**
 * Time format suffixes in moment.js tokens.
 * Covers 12h/24h, with/without seconds, colon and dot separators.
 */
const MOMENT_TIME_SUFFIXES = [
  ' hh:mm:ss A', // 12h with seconds, colons (e.g. 12:00:00 PM)
  ' hh:mm A', // 12h no seconds, colons (e.g. 12:00 PM)
  ' HH:mm:ss', // 24h with seconds, colons (e.g. 14:00:00)
  ' HH:mm', // 24h no seconds, colons (e.g. 14:00)
  ' hh.mm.ss A', // 12h with seconds, dots (e.g. 12.00.00 PM)
  ' hh.mm A', // 12h no seconds, dots (e.g. 12.00 PM)
  ' HH.mm.ss', // 24h with seconds, dots (e.g. 14.00.00)
  ' HH.mm' // 24h no seconds, dots (e.g. 14.00)
]

/**
 * Convert user's dateFormat + timeFormat to a moment.js datetime string.
 * Used as the primary (display) format in getDateTimePickerParseFormats.
 */
export const getMomentDateTimeFormat = (
  user?: { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null
): string => {
  const resolved = resolveUser(user)
  const datePart = getMomentDateFormat(resolved)
  const separator = resolved?.timeFormat ?? ':'
  const timePart = resolved?.timeFormat24h ? `HH${separator}mm${separator}ss` : `hh${separator}mm${separator}ss A`
  return `${datePart} ${timePart}`
}

/**
 * Returns datetime `dateFormat` as an array (moment.js tokens — for lego/ui DatePicker with time).
 * Primary format is the user's configured date+time format (used for display).
 * All date × time combinations are tried when parsing typed input.
 */
export const getDateTimePickerParseFormats = (
  user?: { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null
): string[] => {
  const primary = getMomentDateTimeFormat(user)
  const allDatetimeFormats = ALL_MOMENT_DATE_FORMATS.flatMap((d) => MOMENT_TIME_SUFFIXES.map((t) => d + t))
  return [primary, ...allDatetimeFormats.filter((f) => f !== primary)]
}

/**
 * All supported date formats in date-fns tokens (dd/yyyy lowercase).
 * Used by components that import DatePicker from 'react-datepicker' directly,
 * which uses date-fns internally (unlike @mdsol/lego which uses moment).
 */
const ALL_DATE_FNS_FORMATS = [
  // App display formats (date-fns tokens)
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'yyyy/MM/dd',
  'dd MMM, yyyy',
  'MMM dd, yyyy',
  'dd-MMM-yyyy',
  'MMM-dd-yyyy',
  'ddMMMyyyy',
  'yyyyMMdd',
  // Numeric dash variants
  'dd-MM-yyyy',
  'MM-dd-yyyy',
  'yyyy-MM-dd',
  // Numeric dot variants
  'dd.MM.yyyy',
  'MM.dd.yyyy',
  'yyyy.MM.dd'
]

/**
 * Returns the DatePicker `dateFormat` as an array (date-fns tokens — for react-datepicker directly).
 * Use this in components that import DatePicker from 'react-datepicker' (not from @mdsol/lego).
 * The user's configured format is first (used for display), followed by all other
 * supported formats so that typed input is parsed correctly regardless of what the user types.
 */
export const getDateFnsParseFormats = (user?: { dateFormat?: string } | null): string[] => {
  const resolved = resolveUser(user)
  const primary = resolved?.dateFormat ?? 'MM/dd/yyyy'
  return [primary, ...ALL_DATE_FNS_FORMATS.filter((f) => f !== primary)]
}

/**
 * Time format suffixes in date-fns tokens.
 * Covers 12h/24h, with/without seconds, colon and dot separators.
 */
const DATE_FNS_TIME_SUFFIXES = [
  ' hh:mm:ss aa', // 12h with seconds, colons (e.g. 12:00:00 AM)
  ' hh:mm aa', // 12h no seconds, colons (e.g. 12:00 AM)
  ' HH:mm:ss', // 24h with seconds, colons (e.g. 14:00:00)
  ' HH:mm', // 24h no seconds, colons (e.g. 14:00)
  ' hh.mm.ss aa', // 12h with seconds, dots (e.g. 12.00.00 AM)
  ' hh.mm aa', // 12h no seconds, dots (e.g. 12.00 AM)
  ' HH.mm.ss', // 24h with seconds, dots (e.g. 14.00.00)
  ' HH.mm' // 24h no seconds, dots (e.g. 14.00)
]

/**
 * Returns datetime `dateFormat` as an array (date-fns tokens — for react-datepicker with showTimeInput).
 * Primary format is the user's configured date+time format (used for display).
 * All date × time combinations are tried when parsing typed input — so a user can type
 * "13/05/2026 12:00:00 PM" or "12.05.2026 14:00" regardless of the app's configured format.
 */
export const getDateTimeFnsParseFormats = (
  user?: { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null
): string[] => {
  const resolved = resolveUser(user)
  // Primary format matches user settings (used for display)
  const primary = getDateTimePickerFormat(resolved)
  const allDatetimeFormats = ALL_DATE_FNS_FORMATS.flatMap((d) => DATE_FNS_TIME_SUFFIXES.map((t) => d + t))
  return [primary, ...allDatetimeFormats.filter((f) => f !== primary)]
}

/**
 * Converts a moment date to server format (UTC timestamp)
 * Matches legacy Utils.convertDateForServer
 *
 * @param val - Moment date value
 * @param endOfDay - If true, sets time to 23:59:59, otherwise 00:00:00
 * @returns UTC timestamp or original value if not a moment
 */
export const convertDateForServer = (val: any, endOfDay: boolean = false): any => {
  if (val !== null && val.utc) {
    const utcDateAt = val.utc(true)
    utcDateAt.set(
      endOfDay
        ? { hour: 23, minute: 59, second: 59, millisecond: 0 }
        : { hour: 0, minute: 0, second: 0, millisecond: 0 }
    )
    val = utcDateAt.valueOf()
  }
  return val
}
